<template>
  <WdsStack align-start justify-space-between row>
    <slot name="title"></slot>
    <WdsButton
      v-if="canCreate"
      :disabled="fieldsLoading"
      primary
      class="tw-whitespace-nowrap"
      data-test="add-custom-field-button"
      @click="openCreate = true"
    >
      <WdsStack align-center gap-sm>
        <WdsIcon name="add" :size="16" />
        {{ $t('Add custom field') }}
      </WdsStack>
    </WdsButton>
  </WdsStack>
  <WdsTabs v-model:active-tab-key="activeTab">
    <WdsTab :title="$t('Active')" tab-key="active">
    </WdsTab>
    <WdsTab :title="$t('Archived')" tab-key="archived">
    </WdsTab>
  </WdsTabs>
  <div class="tw-flex tw-justify-center tw-min-h-[100px]">
    <div v-if="fieldsLoading" class="tw-text-center tw-pt-5 tw-pb-5">
      <WdsSpinner :indeterminate="fieldsLoading"></WdsSpinner>
    </div>
    <template v-else>
      <table v-if="visibleFieldObjects.length > 0" class="table table-striped">
        <thead>
          <tr>
            <th class="tw-text-left">{{ $t('Field name') }}</th>
            <th class="tw-text-center">{{ $t('Required') }}</th>
            <th v-if="showTypeColumn" class="tw-text-center">
              {{ $t('Type') }}
            </th>
            <th>{{ $t('Created') }}</th>
            <th class="tw-text-center">{{ $t('Updated at') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="fieldObject in visibleFieldObjects as CustomField[]"
            :key="fieldObject.id!"
            class="tw-font-bold"
          >
            <td>
              {{ fieldObject.title }}
              <CustomFieldVisibilityBadge
                :applies-to="fieldObject.appliesTo"
                :shared-with-clients="fieldObject.sharedWithClients"
                :shared-with-staffing-agencies="fieldObject.sharedWithStaffingAgencies"
              />
            </td>
            <td class="tw-text-center">
              {{ fieldObject.displayValidations() }}
            </td>
            <td v-if="showTypeColumn" class="tw-text-center">
              {{ answeredByLabel(fieldObject) }}
            </td>
            <td>
              {{ fieldObject.createdAt }}
              <template v-if="fieldObject.user">
                <br />
                <span
                  class="tw-text-gray-800"
                  :title="`${fieldObject.user?.name ?? ''} \n${fieldObject.user?.email ?? ''}`"
                >
                  {{ fieldObject.user?.name ?? '' }}
                </span>
              </template>
            </td>

            <td class="tw-text-center">
              {{ fieldObject.updatedAt }}
            </td>
            <td class="tw-text-right">
              <template v-if="fieldObject.viewerCanUpdate">
                <template v-if="destroysInProgress.indexOf(fieldObject.id!) !== -1">
                  <WdsSpinner :indeterminate="true"></WdsSpinner>
                </template>
                <WdsMenu v-else z-index="21" placement="bottom-end">
                  <template #trigger>
                    <WdsButtonGhost :data-test="`custom-field-dropdown-${fieldObject.id}`">
                      <WdsIcon class="tw-cursor-pointer" name="overflow-menu-horizontal" />
                    </WdsButtonGhost>
                  </template>
                  <WdsMenuOption
                    :data-test="`custom-field-edit-${fieldObject.id}`"
                    @click="editCustomField(fieldObject)"
                  >
                    <WdsStack gap-sm align-center>
                      <WdsIcon :size="16" class="tw-cursor-pointer" name="edit" />
                      {{ $t('Edit') }}
                    </WdsStack>
                  </WdsMenuOption>
                  <WdsMenuOption v-if="fieldObject.archivedAt === null"
                  :data-test="`custom-field-archive-${fieldObject.id}`"
                  @click="performArchiveCustomField(fieldObject)">
                    <WdsStack gap-sm align-center>
                      <WdsIcon :size="16" class="tw-cursor-pointer" name="archive" />
                      {{ $t('Archive') }}
                    </WdsStack>
                    </WdsMenuOption>
                    <WdsMenuOption v-else
                    @click="performUnarchiveCustomField(fieldObject)">
                    <WdsStack gap-sm align-center>
                      <WdsIcon :size="16" class="tw-cursor-pointer" name="archive" />
                      {{ $t('Unarchive') }}
                    </WdsStack>
                    </WdsMenuOption>
                  <WdsMenuOption
                    :data-test="`custom-field-delete-${fieldObject.id}`"
                    theme="DESTRUCTIVE"
                    :disabled="destroysInProgress.indexOf(fieldObject.id!) !== -1"
                    @click="performDeleteCustomFieldAction(fieldObject)"
                  >
                    <WdsStack gap-sm align-center>
                      <WdsIcon :size="16" class="tw-cursor-pointer" name="trash-can" />
                      {{ $t('Delete') }}
                    </WdsStack>
                  </WdsMenuOption>
                </WdsMenu>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <BaseHtml
        v-else
        class="tw-text-center tw-pt-5 tw-pb-5"
        :html="
          activeTab === 'archived'
            ? $t('No custom fields have been archived')
            :
          canCreate
            ? $t('Click Add custom field above to start adding custom fields')
            : $t('No custom fields have been added')
        "
      ></BaseHtml>
    </template>
  </div>
  <AddCustomField
    v-if="canCreate"
    :show="openCreate"
    :applies-to="appliesTo"
    :account-id="accountId"
    :visibility="props.visibility"
    :worker-onboarding-enabled="props.workerOnboardingEnabled"
    :is-supplier="props.isSupplier"
    :is-company="props.isCompany"
    @cancel="cancelCreate"
    @success="successfulCreate"
  />

  <template v-if="fieldIdToEdit !== null">
    <EditCustomField
      :field-id="fieldIdToEdit"
      :applies-to="appliesTo"
      :show="true"
      :visibility="props.visibility"
      :worker-onboarding-enabled="props.workerOnboardingEnabled"
      :is-supplier="props.isSupplier"
      :is-company="props.isCompany"
      @cancel="cancelEdit"
      @success="successfulEdit"
    />
  </template>
  <template v-if="fieldToDelete">
    <ConfirmDeleteCustomField
      :show="true"
      :field="fieldToDelete"
      :confirm="confirmDeleteCustomField"
      :cancel="cancelDelete"
      :applies-to="appliesTo"
    />
  </template>
</template>

<script setup lang="ts">
import { computed, reactive, ref, Ref } from 'vue'
import {
  WdsButton,
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsSpinner,
  WdsStack,
  WdsTabs,
  WdsTab,
} from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { isApolloError } from '@apollo/client/core'
import { $t } from '@/composables/translation'
import AddCustomField from '@/components/Fields/AddCustomField.vue'
import ConfirmDeleteCustomField from '@/components/Fields/ConfirmDeleteCustomField.vue'
import EditCustomField from '@/components/Fields/EditCustomField.vue'
import {
  CustomFieldsListQuery,
  InheritedCustomFieldsListQuery,
} from '@/components/Fields/graphql/queries'
import { DeleteCustomFieldMutation, SetCustomFieldsArchived } from '@/components/Fields/graphql/mutations'
import { newInstanceOfCustomField } from '@/components/Fields/utils/CustomFieldUtils'
import { CustomField } from '@/components/Fields/dataObjects/CustomField'
import BaseHtml from '@/components/BaseHtml.vue'
import { CustomFieldVisibility, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { useFragment } from '@/gql'
import { CustomFieldListFragment } from '@/components/Fields/graphql/fragments'
import CustomFieldVisibilityBadge from '@/components/Fieldable/CustomFieldVisibilityBadge.vue'
import { FormValidation } from '@/utils/formValidation'
import enums from '@/utils/enum'
import { NetworkError } from '@/utils/notification'

interface Props {
  accountId: string
  appliesTo: TypeSupportingCustomFieldValues
  visibility?: CustomFieldVisibility
  inheritedOnly: boolean
  canCreate?: boolean
  workerOnboardingEnabled?: boolean
  isSupplier?: boolean
  isCompany?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  inheritedOnly: false,
  // eslint-disable-next-line vue/no-boolean-default
  canCreate: true,
  visibility: CustomFieldVisibility.Internal,
  workerOnboardingEnabled: false,
})
const isClassification = computed(
  () => props.appliesTo === TypeSupportingCustomFieldValues.ClassificationInput,
)

const showTypeColumn = computed(
  () =>
    isClassification.value ||
    (props.workerOnboardingEnabled &&
      (props.appliesTo === TypeSupportingCustomFieldValues.TrustedContact ||
        props.appliesTo === TypeSupportingCustomFieldValues.CompanyRecruiter)),
)

const isCompanyRecruiter = computed(
  () => props.appliesTo === TypeSupportingCustomFieldValues.CompanyRecruiter,
)

function answeredByLabel(field: { workerInputAllowed: boolean; recruiterInputAllowed: boolean }) {
  if (isCompanyRecruiter.value) {
    return field.recruiterInputAllowed ? $t('Staffing agency onboarding') : $t('Internal')
  }

  if (isClassification.value) {
    return field.workerInputAllowed ? $t('Worker') : $t('Internal')
  }

  return field.workerInputAllowed ? $t('Worker onboarding') : $t('Internal')
}

const openCreate: Ref<boolean> = ref(false)
const fieldIdToEdit: Ref<string | null> = ref(null)
const deletesNeedingConfirmation = reactive<string[]>([])
const destroysInProgress = reactive<string[]>([])

const query = computed(
  () => (props.inheritedOnly ? InheritedCustomFieldsListQuery : CustomFieldsListQuery) as any,
)

const {
  result: customFieldQueryResult,
  refetch,
  loading: fieldsLoading,
} = useQuery(
  query.value,
  {
    accounts: [props.accountId],
    appliesTo: [props.appliesTo],
    includeArchived: true,
  },
  {
    clientId: 'publicApiCustomFields',
  },
)

const { mutate: destroyCustomField } = useMutation(DeleteCustomFieldMutation, {
  update(cache, result) {
    const deleteCustomField = result.data?.deleteCustomField
    if (deleteCustomField) {
      cache.evict({ id: cache.identify(deleteCustomField) })
      destroysInProgress.splice(destroysInProgress.indexOf(deleteCustomField.id), 1)
    }
  },
  clientId: 'publicApiCustomFields',
})

const { mutate: setCustomFieldsArchived } = useMutation(SetCustomFieldsArchived, {clientId: 'publicApiCustomFields'})

const fieldObjects = computed<CustomField[]>(
  () =>
    customFieldQueryResult.value?.customFields.data.map((field) => {
      const fieldTyped = useFragment(CustomFieldListFragment, field)
      return newInstanceOfCustomField(fieldTyped, fieldTyped.fieldType)
    }) || [],
)
const activeTab = ref<'active'| 'archived'>('active')
const activeFieldObjects = computed<CustomField[]>(() =>
  fieldObjects.value.filter( ({archivedAt}) => archivedAt === null)
)
const archivedFieldObjects = computed<CustomField[]>(() =>
  fieldObjects.value.filter( ({archivedAt}) => archivedAt !== null)
)
const visibleFieldObjects = computed<CustomField[]>(() =>
  activeTab.value === 'active'? activeFieldObjects.value : archivedFieldObjects.value
)
const fieldToDelete = computed<CustomField | null | undefined>(() => {
  if (deletesNeedingConfirmation.length === 0) {
    return null
  }
  const idToDelete = deletesNeedingConfirmation[0]

  return fieldObjects.value.find((field: CustomField) => field.id === idToDelete)
})

function successfulCreate() {
  refetch()
  openCreate.value = false
}

function cancelCreate() {
  openCreate.value = false
}

function cancelEdit() {
  fieldIdToEdit.value = null
}

function successfulEdit() {
  refetch()
  fieldIdToEdit.value = null
}

function editCustomField(field: CustomField) {
  fieldIdToEdit.value = field.id
}

// A refusal, such as a field that rate cards key on, arrives as a validation
// error carrying a message already translated for the viewer.
function refusal(exception: unknown, inputKey: string): string | undefined {
  if (!(exception instanceof Error) || !isApolloError(exception)) {
    return undefined
  }

  const errors = FormValidation.fromGraphqlErrors(exception.graphQLErrors) as Record<
    string,
    string[]
  > | null

  return errors?.[inputKey]?.[0]
}

async function performArchiveCustomField(field: CustomField) {
  try {
    await setCustomFieldsArchived(
      {
        input: {
          customFields: [field.id!],
          archived: true,
        },
      },
      { context: { skipGlobalErrorHandling: true } },
    )
  } catch (error: any) {
    const archiveRefusal = refusal(error, 'input.customFields')
    if (archiveRefusal) {
      new NetworkError({ msg: archiveRefusal, severity: enums.notification.severity.WARNING }).dispatch()

      return
    }

    new NetworkError({ ex: error }).dispatch()
  }
}

async function performUnarchiveCustomField(field: CustomField) {
  try {
    await setCustomFieldsArchived(
      {
        input: {
          customFields: [field.id!],
          archived: false,
        },
      },
      { context: { skipGlobalErrorHandling: true } },
    )
  } catch (error: any) {
    new NetworkError({ ex: error }).dispatch()
  }
}

async function performDestroyCustomField(field: CustomField) {
  destroysInProgress.push(field.id as string)

  try {
    // Every failure is reported below, so the global error toast is skipped.
    await destroyCustomField(
      {
        input: {
          customField: field.id!,
        },
      },
      { context: { skipGlobalErrorHandling: true } },
    )
  } catch (exception: any) {
    destroysInProgress.splice(destroysInProgress.indexOf(field.id as string), 1)

    const deleteRefusal = refusal(exception, 'input.customField')
    if (deleteRefusal) {
      new NetworkError({ msg: deleteRefusal, severity: enums.notification.severity.WARNING }).dispatch()

      return
    }

    new NetworkError({
      msg: $t('An error occurred when trying to delete a custom field'),
      severity: enums.notification.severity.CRITICAL,
      ex: exception,
    }).dispatch()
  }
}

function performDeleteCustomFieldAction(field: CustomField) {
  if (field.isUsedInFieldValues) {
    deletesNeedingConfirmation.push(field.id as string)
  } else {
    performDestroyCustomField(field)
  }
}

function confirmDeleteCustomField(field: CustomField) {
  deletesNeedingConfirmation.splice(deletesNeedingConfirmation.indexOf(field.id as string), 1)
  performDestroyCustomField(field)
}

function cancelDelete(field: CustomField) {
  deletesNeedingConfirmation.splice(deletesNeedingConfirmation.indexOf(field.id as string), 1)
}
</script>

<style lang="scss" scoped>
tr.row-disabled {
  position: relative;

  &:after {
    position: absolute;
    inset: 0;
    background-color: #cccccc;
    opacity: 0.5;
  }
}

table .btn-group .btn .preloader.pls-blue {
  position: static;
  margin: 0;
  width: 1em;
}

table tr td {
  vertical-align: middle;
}
</style>